import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Users, 
  TrendingUp, 
  Filter,
  Search,
  Calendar,
  Award,
  Eye
} from 'lucide-react';
import api from '../api';

const NotesTab = () => {
  const [notes, setNotes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedNiveau, setSelectedNiveau] = useState('');
  const [selectedAnnee, setSelectedAnnee] = useState('');
  const [annees, setAnnees] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Récupérer les années académiques
      const anneesResponse = await api.get('/etablissement/annees-academiques/');
      setAnnees(anneesResponse.data);
      
      // Récupérer l'année active par défaut
      const anneeActive = anneesResponse.data.find(a => a.status) || anneesResponse.data[0];
      if (anneeActive) {
        setSelectedAnnee(anneeActive.id);
        await fetchNotesByAnnee(anneeActive.id);
      }
      
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error('Erreur fetchData:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotesByAnnee = async (anneeId) => {
    try {
      const response = await api.get(`/note/moyennes-semestres/?annee_academique_id=${anneeId}`);
      setNotes(response.data.data || response.data);
      
              // Extraire les classes et niveaux uniques
        const classesUniques = [...new Set((response.data.data || response.data)?.map(n => n.classe) || [])];
        const niveauxUniques = [...new Set((response.data.data || response.data)?.map(n => n.classe?.niveau) || [])];
      
      setClasses(classesUniques);
      setNiveaux(niveauxUniques);
      
    } catch (err) {
      console.error('Erreur fetchNotesByAnnee:', err);
    }
  };

  const handleAnneeChange = async (anneeId) => {
    setSelectedAnnee(anneeId);
    await fetchNotesByAnnee(anneeId);
  };

  const filteredNotes = notes.filter(note => {
    if (selectedClasse && note.classe?.id !== parseInt(selectedClasse)) return false;
    if (selectedNiveau && note.classe?.niveau?.id !== parseInt(selectedNiveau)) return false;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        note.etudiant?.nom?.toLowerCase().includes(searchLower) ||
        note.etudiant?.prenom?.toLowerCase().includes(searchLower) ||
        note.etudiant?.matricule?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const groupedByClasse = filteredNotes.reduce((acc, note) => {
    const classeId = note.classe?.id;
    if (!acc[classeId]) {
      acc[classeId] = {
        classe: note.classe,
        notes: [],
        total: 0,
        moyenne_classe: 0,
        excellents: 0,
        tres_biens: 0,
        biens: 0,
        passables: 0,
        insuffisants: 0
      };
    }
    acc[classeId].notes.push(note);
    acc[classeId].total++;
    
    // Compter les mentions
    switch (note.mention_semestre) {
      case 'EXCELLENT':
        acc[classeId].excellents++;
        break;
      case 'TRES_BIEN':
        acc[classeId].tres_biens++;
        break;
      case 'BIEN':
        acc[classeId].biens++;
        break;
      case 'PASSABLE':
        acc[classeId].passables++;
        break;
      case 'INSUFFISANT':
        acc[classeId].insuffisants++;
        break;
    }
    
    return acc;
  }, {});

  // Calculer les moyennes de classe
  Object.values(groupedByClasse).forEach(groupe => {
    if (groupe.notes.length > 0) {
      const totalMoyennes = groupe.notes.reduce((sum, note) => sum + (note.moyenne_generale || 0), 0);
      groupe.moyenne_classe = (totalMoyennes / groupe.notes.length).toFixed(2);
    }
  });

  const getMentionColor = (mention) => {
    switch (mention) {
      case 'EXCELLENT':
        return 'text-purple-600 bg-purple-100';
      case 'TRES_BIEN':
        return 'text-blue-600 bg-blue-100';
      case 'BIEN':
        return 'text-green-600 bg-green-100';
      case 'ASSEZ_BIEN':
        return 'text-yellow-600 bg-yellow-100';
      case 'PASSABLE':
        return 'text-orange-600 bg-orange-100';
      case 'INSUFFISANT':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
        <span className="ml-3 text-gray-600">Chargement des notes...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 mb-4">
          <BookOpen className="h-12 w-12 mx-auto mb-2" />
          <p className="text-lg font-medium">{error}</p>
        </div>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#202654]/90 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Filtres</h3>
          <Filter className="h-5 w-5 text-gray-400" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Recherche */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Nom, prénom, matricule..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654]"
              />
            </div>
          </div>

          {/* Filtre par année académique */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Année Académique
            </label>
            <select
              value={selectedAnnee}
              onChange={(e) => handleAnneeChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654]"
            >
              <option value="">Toutes les années</option>
              {annees.map((annee) => (
                <option key={annee.id} value={annee.id}>
                  {annee.annee_debut}-{annee.annee_fin}
                </option>
              ))}
            </select>
          </div>

          {/* Filtre par niveau */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Niveau
            </label>
            <select
              value={selectedNiveau}
              onChange={(e) => setSelectedNiveau(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654]"
            >
              <option value="">Tous les niveaux</option>
              {niveaux.map((niveau) => (
                <option key={niveau.id} value={niveau.id}>
                  {niveau.libelle}
                </option>
              ))}
            </select>
          </div>

          {/* Filtre par classe */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Classe
            </label>
            <select
              value={selectedClasse}
              onChange={(e) => setSelectedClasse(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654]"
            >
              <option value="">Toutes les classes</option>
              {classes.map((classe) => (
                <option key={classe.id} value={classe.id}>
                  {classe.libelle}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Statistiques générales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Notes</p>
              <p className="text-2xl font-bold text-gray-900">{filteredNotes.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <BookOpen className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Classes</p>
              <p className="text-2xl font-bold text-gray-900">{Object.keys(groupedByClasse).length}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Moyenne Générale</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredNotes.length > 0 
                  ? (filteredNotes.reduce((sum, note) => sum + (note.moyenne_generale || 0), 0) / filteredNotes.length).toFixed(2)
                  : '0.00'
                }
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Excellents</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredNotes.filter(n => n.mention_semestre === 'EXCELLENT').length}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Award className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Notes par classe */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Notes Définitives par Classe</h3>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <BookOpen className="h-4 w-4" />
            <span>{Object.keys(groupedByClasse).length} classes</span>
          </div>
        </div>

        {Object.keys(groupedByClasse).length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune note trouvée</h3>
            <p className="text-gray-500">
              {selectedAnnee || selectedNiveau || selectedClasse || searchTerm
                ? 'Aucune note ne correspond aux filtres sélectionnés.'
                : 'Aucune note n\'a été calculée pour le moment.'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.values(groupedByClasse).map((groupe, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* En-tête de la classe */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        {groupe.classe?.libelle}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {groupe.classe?.niveau?.libelle} • {groupe.total} étudiants
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Moyenne de classe</p>
                      <p className="text-xl font-bold text-[#202654]">{groupe.moyenne_classe}/20</p>
                    </div>
                  </div>
                </div>

                {/* Statistiques de la classe */}
                <div className="px-6 py-4 bg-white">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                    <div>
                      <p className="text-sm text-gray-600">Excellents</p>
                      <p className="text-lg font-semibold text-purple-600">{groupe.excellents}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Très bien</p>
                      <p className="text-lg font-semibold text-blue-600">{groupe.tres_biens}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Bien</p>
                      <p className="text-lg font-semibold text-green-600">{groupe.biens}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Passable</p>
                      <p className="text-lg font-semibold text-orange-600">{groupe.passables}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Insuffisant</p>
                      <p className="text-lg font-semibold text-red-600">{groupe.insuffisants}</p>
                    </div>
                  </div>
                </div>

                {/* Liste des étudiants */}
                <div className="px-6 py-4">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 text-sm font-medium text-gray-700">Rang</th>
                          <th className="text-left py-2 text-sm font-medium text-gray-700">Étudiant</th>
                          <th className="text-left py-2 text-sm font-medium text-gray-700">Matricule</th>
                          <th className="text-center py-2 text-sm font-medium text-gray-700">Moyenne</th>
                          <th className="text-center py-2 text-sm font-medium text-gray-700">Mention</th>
                          <th className="text-center py-2 text-sm font-medium text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupe.notes
                          .sort((a, b) => (b.moyenne_generale || 0) - (a.moyenne_generale || 0))
                          .map((note, noteIndex) => (
                          <tr key={note.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 text-sm font-medium text-gray-900">
                              {noteIndex + 1}
                            </td>
                            <td className="py-3 text-sm text-gray-900">
                              {note.etudiant?.nom} {note.etudiant?.prenom}
                            </td>
                            <td className="py-3 text-sm text-gray-600">
                              {note.etudiant?.matricule}
                            </td>
                            <td className="py-3 text-sm text-center font-semibold text-gray-900">
                              {note.moyenne_generale?.toFixed(2) || '0.00'}/20
                            </td>
                            <td className="py-3 text-sm text-center">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMentionColor(note.mention_semestre)}`}>
                                {note.mention_semestre?.replace('_', ' ') || 'N/A'}
                              </span>
                            </td>
                            <td className="py-3 text-sm text-center">
                              <button className="p-1 text-[#202654] hover:bg-[#202654] hover:text-white rounded transition-colors">
                                <Eye className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesTab; 